import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { allowCheckout } from "@/app/_module/utils/nativeCheckoutGuard";
import api from '@/app/_module/services/api';
import { getApiUrl } from '@/app/_module/services/config';
import { handleApiCall } from '@/app/_module/utils/apiResponseHandler';

interface FaxAccessResponse {
  hasAccess: boolean;
  accountType: string;
  planName: string;
  faxNumber: string | null;
  reason: string | null;
  requiresPayment?: boolean;
}

interface WrappedFaxAccessResponse {
  statusCode?: number;
  data?: FaxAccessResponse;
}

interface FaxNumberResponse {
  faxNumber: string | null;
  status: string;
  provisionedAt: string | null;
}

interface ProvisionFaxNumberResponse {
  faxNumber: string;
  status: string;
  provisionedAt: string;
  alreadyExisted: boolean;
}

interface FaxCheckoutResponse {
  checkoutUrl: string;
  sessionId: string;
  transactionId: string;
}

interface FaxFulfillResponse {
  faxNumber: string;
  transactionId: string;
}

/**
 * Hook to check if user has fax access and get their provisioned fax number
 * Returns access status, plan info, and fax number (for Basic/Full-Fledged users)
 */
export function useFaxAccess() {
  // Check fax access and get all details in one call
  const { data: accessData, isLoading, error, refetch } = useQuery({
    queryKey: ['fax-access', 'v5'], // v5 - added requiresPayment + checkout/fulfill
    queryFn: () => handleApiCall<FaxAccessResponse>(() => api.get({
      url: getApiUrl('/fax-access/check-access'),
      auth: true,
    })),
    staleTime: 0, // Real-time: always fetch fresh data for fax access/provisioning status
    gcTime: 5 * 60 * 1000, // 5 minutes - keep in cache for background
    retry: 1,
  });

  // Handle both wrapped and unwrapped responses
  const wrappedData = accessData as WrappedFaxAccessResponse | FaxAccessResponse | undefined;
  const actualData = wrappedData && 'statusCode' in wrappedData && wrappedData.data ? wrappedData.data : wrappedData as FaxAccessResponse | undefined;

  const queryClient = useQueryClient();

  // Mutation to manually provision fax number (free — non-enterprise users)
  const provisionMutation = useMutation({
    mutationFn: () => handleApiCall<ProvisionFaxNumberResponse>(() => api.post({
      url: getApiUrl('/fax-access/provision-my-number'),
      auth: true,
    })),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fax-access'] });
    },
  });

  // Mutation to create Stripe checkout session (enterprise users — $5/month)
  const checkoutMutation = useMutation({
    mutationFn: () => handleApiCall<FaxCheckoutResponse>(() => api.post({
      url: getApiUrl('/fax-access/checkout'),
      auth: true,
    })),
    onSuccess: (data) => {
      const checkoutData = data as any;
      const url = checkoutData?.checkoutUrl || checkoutData?.data?.checkoutUrl;
      if (url) {
        if (!allowCheckout("payment")) return;
        window.location.href = url;
      }
    },
  });

  // Mutation to fulfill fax checkout after Stripe redirect (fallback if webhook hasn't fired yet)
  const fulfillMutation = useMutation({
    mutationFn: (sessionId: string) => handleApiCall<FaxFulfillResponse>(() => api.post({
      url: getApiUrl(`/fax-access/checkout/fulfill/${sessionId}`),
      auth: true,
    })),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fax-access'] });
    },
  });

  return {
    // Access status
    hasAccess: actualData?.hasAccess ?? false,
    accountType: actualData?.accountType ?? 'trial',
    planName: actualData?.planName ?? 'Trial',
    reason: actualData?.reason,
    requiresPayment: actualData?.requiresPayment ?? false,

    // Fax number (only available if hasAccess is true)
    faxNumber: actualData?.faxNumber ?? null,

    // Query state
    isLoading,
    error,
    refetch,

    // Manual provisioning (free)
    provisionFaxNumber: provisionMutation.mutateAsync,
    isProvisioning: provisionMutation.isPending,
    provisionError: provisionMutation.error,

    // Stripe checkout (enterprise — $5/month)
    startFaxCheckout: checkoutMutation.mutateAsync,
    isCheckingOut: checkoutMutation.isPending,
    checkoutError: checkoutMutation.error,

    // Fulfillment (after Stripe redirect)
    fulfillFaxCheckout: fulfillMutation.mutateAsync,
    isFulfilling: fulfillMutation.isPending,
    fulfillError: fulfillMutation.error,
  };
}
